import React, { FC } from "react";
import { _throw, IllegalStateError } from "shared-ts-utils-core";
import { UserRegisterRequest } from "./UserRegisterRequest";
import { ProblemDetailsFetchError } from "shared-ts-utils-fetch";
import { Property } from "shared-ts-property";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { Button, Stack, Toolbar } from "@mui/material";
import { useUserRegistrationDao } from "./useUserRegistrationDao";
import { UserRegistrationState } from "./UserRegistrationState";
import { useTranslation } from "react-i18next";
import { PropertiesCard } from "shared-ts-ui-properties-card";
import { ConsentBurnTimeIsSavedProperty } from "firefly-my-chimney-ts-pages";
import { useDense } from "shared-ts-ui-responsive";
import { McUserDto } from "firefly-my-chimney-ts-user";
import { resolvePasswordErrorMessages } from "shared-ts-ui-identity";

const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldComponents: {
    Property,
  },
  formComponents: {
    Button,
  },
  fieldContext,
  formContext,
});

export type RegisterUserFormValues = {
  email: string;
  password: string;
  confirmPassword: string;
  consentBurnTimeIsSaved: boolean;
};

export interface UserRegistrationFormProps {
  token: string;
  initialFormValues: RegisterUserFormValues;
  onRegistrationStateChange: (
    state: UserRegistrationState,
    errorMessage?: string,
    user?: McUserDto
  ) => void;
}

export const UserRegistrationForm: FC<UserRegistrationFormProps> = (props) => {
  const dense = useDense();
  const userRegistrationDao = useUserRegistrationDao(props.token);
  const { t } = useTranslation("my-chimney-ts-translations");
  const { t: tSharedIdentity } = useTranslation("shared-ts-ui-identity");
  const form = useAppForm({
    defaultValues: props.initialFormValues,
    validators: {
      // Pass a schema or function to validate
      /*onChange: z.object({
        username: z.string(),
        age: z.number().min(13),
      }),*/
    },
    onSubmit: async ({ value }) => {
      // Do something with form data
      /*if (registeringUser == null) {
        throw new IllegalStateError("registeringUser was null");
      }*/
      const request: UserRegisterRequest = {
        email: value.email,
        password: value.password,
        consentBurnTimeIsSaved: value.consentBurnTimeIsSaved,
      };
      try {
        const mcUser =
          (await userRegistrationDao?.registerUser(request)) ??
          _throw(new IllegalStateError("userRegistrationDao not initialized"));

        props.onRegistrationStateChange("success", undefined, mcUser);
      } catch (e) {
        console.error(e);
        if (e instanceof ProblemDetailsFetchError) {
          const errorMessages = resolvePasswordErrorMessages(
            e,
            tSharedIdentity
          );
          const errorMessage = errorMessages.join(",\n");
          props.onRegistrationStateChange("error", errorMessage);
        } else if (e instanceof Error) {
          props.onRegistrationStateChange("error", e.message);
        } else {
          props.onRegistrationStateChange("error");
        }
      }
    },
  });

  const handleCancel = () => {
    window.location.assign("/");
  };

  return (
    <form
      autoComplete={"on"}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <Stack direction={"column"} useFlexGap sx={{ gap: 1 }}>
        <PropertiesCard
          header={{
            title: tSharedIdentity("Register yourself as a user"),
          }}
          sx={{
            justifyContent: "center",
          }}
        >
          <form.AppField
            name={"email"}
            children={(field) => (
              <Property
                name={field.name}
                label={tSharedIdentity("Email")}
                type={"string"}
                value={field.state.value}
                autoComplete={"username"}
                inputProps={{
                  id: "username",
                  type: "email",
                  inputMode: "email",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
                mode={"edit"}
                fullWidth
                autoFocus
                onValueChange={(value) => field.setValue(value as string)}
              />
            )}
          />
          <form.AppField
            name={"password"}
            children={(field) => (
              <Property
                name={field.name}
                label={tSharedIdentity("Password")}
                type={"password"}
                value={field.state.value}
                autoComplete={"new-password"}
                mode={"edit"}
                fullWidth
                inputProps={{
                  id: "new-password",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
                onValueChange={(value) => field.setValue(value as string)}
              />
            )}
          />
          <form.AppField
            name={"confirmPassword"}
            children={(field) => (
              <Property
                name={field.name}
                label={tSharedIdentity("Confirm Password")}
                type={"password"}
                value={field.state.value}
                autoComplete={"new-password"}
                mode={"edit"}
                fullWidth
                inputProps={{
                  id: "confirm-password",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
                onValueChange={(value) => field.setValue(value as string)}
              />
            )}
          />
          <form.AppField
            name={"consentBurnTimeIsSaved"}
            children={(field) => (
              <ConsentBurnTimeIsSavedProperty
                value={field.state.value}
                onChange={(name, value) => field.setValue(value)}
              />
            )}
          />
        </PropertiesCard>
        <Toolbar
          variant={dense ? "dense" : "regular"}
          disableGutters
          sx={{ gap: 2 }}
        >
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <>
                <Button
                  type="submit"
                  color={"secondary"}
                  variant={"contained"}
                  disabled={!canSubmit}
                  loading={isSubmitting}
                >
                  {tSharedIdentity("Register")}
                </Button>
                <Button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t("Cancel")}
                </Button>
              </>
            )}
          </form.Subscribe>
        </Toolbar>
      </Stack>
    </form>
  );
};
